import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Button,
  Avatar,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Badge,
  IconButton,
} from "@mui/material";
import { updateProfileImage } from "../services/UserProfileApi";
import { Close } from "@mui/icons-material";
import { compressImage } from "../utils/ImageCompression";

export default function ProfilePictureSetting({ setStep}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setImageSrc(reader.result);
        setIsCropping(true);
      };
    }
  };

  const getCroppedImage = async () => {
    const image = new Image();
    image.src = imageSrc;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const { width, height, x, y } = croppedAreaPixels;

    canvas.width = width;
    canvas.height = height;

    image.onload = async () => {
      ctx.drawImage(image, x, y, width, height, 0, 0, width, height);
      canvas.toBlob(async (blob) => {
        const compressedBlob = await compressImage(blob); // Use your utility function
        const file = new File([compressedBlob], "profile.jpg", {
          type: "image/jpeg",
        });
        setCroppedImage(URL.createObjectURL(compressedBlob));
        setIsCropping(false);
        handleProfilePictureSave(file);
      }, "image/jpeg");
    };
  };

  // Function to handle saving the profile picture
  const handleProfilePictureSave = (file) => {
    let formData;
    if (file) {
      formData = new FormData();
      formData.append("profile_pic", file);
    } else {
      formData = { profile_pic: null };
    }

    setIsUploading(true);
    updateProfileImage(formData)
      .then((response) => {
        console.log("Profile picture updated: ", response);
      })
      .catch((error) => {
        console.error("Error updating profile picture: ", error);
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleDeleteProfilePicture = () => {
    setCroppedImage(null);
    handleProfilePictureSave(null
    );
  }


  return (
    <React.Fragment>
      {/* Profile Picture Preview */}
      <Box sx={{ textAlign: "center" }}>
        {croppedImage ? (
          <Badge
            overlap="circular"
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            badgeContent={
              <IconButton
                color="primary"
                size="small"
                sx={{ backgroundColor: "#FFEEEE" }}
                onClick={handleDeleteProfilePicture}
              >
                <Close />
              </IconButton>
            }
          >
            <Avatar
              src={croppedImage}
              alt="Profile"
              sx={{ width: 120, height: 120, mx: "auto" }}
            />
          </Badge>
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontSize: 12 }}
          >
            No picture selected
          </Typography>
        )}
      </Box>

      {/* File Input */}
      <Button
        variant="outlined"
        component="label"
        fullWidth
        sx={{ textTransform: "none", mt: 2 }}
      >
        {croppedImage ? "Change Picture" : "Upload Picture"}
        <input
          type="file"
          hidden
          accept="image/*"
          onChange={handleFileChange}
        />
      </Button>

      {/* Next Button */}
      <Button
        size="small"
        variant="contained"
        onClick={() => setStep(3)}
        sx={{
          textTransform: "none",
          position: "absolute",
          top: 15,
          right: 10,
        }}
        disableElevation
      >
        {croppedImage ? "Next" : "Skip"}
      </Button>

      {/* Cropping Dialog */}
      <Dialog
        open={isCropping}
        fullWidth
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
        aria-describedby="form-dialog-description"
        aria-hidden={!isCropping}
      >
        <DialogTitle id="form-dialog-title" sx={{bgcolor: "background.paper"}}>Adjust Profile Picture</DialogTitle>

        <DialogContent>
          <Box sx={{ position: "relative", width: "100%", height: 300, bgcolor: "background.paper" }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{bgcolor: "background.paper"}}>
          <Button
            onClick={() => setIsCropping(false)}
            disableElevation
            sx={{ textTransform: "none" }}
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={getCroppedImage}
            variant="contained"
            size="small"
            disableElevation
            sx={{ textTransform: "none" }}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : null}
          >
              Save
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
